import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface SerpResult {
  rank: number;
  title: string;
  link: string;
  snippet: string;
}

interface SearchCache {
  results: SerpResult[];
  timestamp: number;
  location: string;
}

interface SearchState {
  // Cache object with query as key
  searchCache: Record<string, SearchCache>;
  
  // Add results to cache
  addToCache: (query: string, results: SerpResult[], location: string) => void;
  
  // Get results from cache
  getFromCache: (query: string, location: string) => SerpResult[] | null;
  
  // Clear specific cache entry
  clearCache: (query: string) => void;
  
  // Clear entire cache
  clearAllCache: () => void;
}

// Cache expiration time (24 hours)
const CACHE_EXPIRATION = 24 * 60 * 60 * 1000;

export const useSearchStore = create<SearchState>()(
  persist(
    (set, get) => ({
      searchCache: {},

      addToCache: (query, results, location) => {
        set((state) => ({
          searchCache: {
            ...state.searchCache,
            [query]: {
              results,
              location,
              timestamp: Date.now(),
            },
          },
        }));
      },

      getFromCache: (query, location) => {
        const cache = get().searchCache[query];
        
        // If no cache exists, return null
        if (!cache) return null;

        // If location doesn't match, return null
        if (cache.location !== location) return null;

        // Check if cache is expired
        const isExpired = Date.now() - cache.timestamp > CACHE_EXPIRATION;
        
        if (isExpired) {
          // Clear expired cache
          get().clearCache(query);
          return null;
        }

        return cache.results;
      },

      clearCache: (query) => {
        set((state) => {
          const { [query]: removed, ...remaining } = state.searchCache;
          return { searchCache: remaining };
        });
      },

      clearAllCache: () => {
        set({ searchCache: {} });
      },
    }),
    {
      name: 'search-cache', // localStorage key
      version: 1, // version number for migration
    }
  )
);